import { Box, Grid, Collapse, useTheme, Link, Typography, Chip, useMediaQuery, MenuItem, ListItemIcon } from "@mui/material";
import Header from "../../common/Header";
import Card from "../../common/Card";
import Button from "../../common/Button";
import { ArchiveOutlined, Checklist, CloudUpload, DownloadOutlined, ExitToApp, KeyboardArrowDown, ModeOutlined, PersonAddAltOutlined, SearchOutlined, TrackChanges, VisibilityOutlined, WysiwygOutlined } from "@mui/icons-material";
import Table from "../../common/CustomTable";
import { useLocation, useNavigate } from "react-router-dom";
import { usePracticeSlice } from "../../../redux/slice/practiceSlice";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { useForm } from "react-hook-form";
import { fullName, hideElement, sortNames } from "../../../utils/Utility";
import { createListSort } from "../../../utils/listSort";
import { showHideLoadingDialog, SingleAlertInfo } from "../../../redux/slice/commonSlice";
import CustomIconButton from "../../common/CustomIconButton";
import { CAN_ACCESS_APPLICATION, EXPORTABLE_ITEMS, PROVIDER_LIST_ACTIONS, USER_ROLES } from "../../../constants/applicationConstants";
import { getSearchCriteriaFromSessionStorage, setSearchCriteriaToSessionStorage } from "../../../utils/StorageUtils";
import { PracticeInfo } from "../../../redux/slice/practice/practiceRegistrationSlice";
import { getPracticesByUserRoleUserSlice, useUserReducer } from "../../../redux/slice/userSlice";
import CustomMultipleDropDown from "../../common/CustomMultipleDropDown";
import { selectIsValueFoundInToken, setSelectedModule, useAuthSlice } from "../../../redux/slice/authSlice";
import { fetchProviderTemplatesByProvider, fetchUsedPracticeProviderIDs, resetSearchedProviderList, SearchProviderRequest, searchProviders, usePracticeProvidersSlice } from "../../../redux/slice/practice/practiceProvidersSlice";
import CustomTextField from "../../common/CustomTextField";
import OptionsPopup, { OptionPopupObjectProps } from "../../common/OptionsPopup";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { tokens } from "../../../theme";
import { useNavigation } from "../../../hooks/useNavigation";
import { useFilterPanelFocus } from "../../../hooks/useFilterPanelFocus";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import StyledMenu from "../../common/StyledMenu";
import ExportToExcel from "../../common/ExportToExcel";
import { useModalStyles } from "../../common/useModalStyles";

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "center" | "left"; // Adjust the align property type
  format?: (value: number) => string;
}

type Option = {
  value: string,
  label: string
};

// Define columns and rows data
const columns: Column[] = [
  { id: "practiceName", label: "Practice", minWidth: 100 },
  { id: "providerName", label: "Provider Name", minWidth: 100 },
  // { id: "lastName", label: "last Name", minWidth: 100 },
  { id: "npi", label: "NPI", minWidth: 100 },
  { id: "ptan", label: "Ptan", minWidth: 100 },
  { id: "taxId", label: "Tax ID", minWidth: 100 },
  { id: "email", label: "Email", minWidth: 100 },
  { id: "userCreated", label: "User Account Created", minWidth: 100 },
  // { id: "templateAvailability", label: "Template Availability", minWidth: 100 },
  { id: "action", label: "Action", minWidth: 50, align: "left", },
];

const ITEMS_PER_PAGE = 20;

const providerListSort = createListSort(
  ["practiceName", "providerName", "email"] as const,
  {
    practiceName: "practiceName",
    providerName: "providerName",
    email: "email",
  }
);

const ProviderList = () => {

  const location = useLocation();
  const { navigate } = useNavigation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { primaryBtnSx, tokens: modalTokens } = useModalStyles();

  const { control, handleSubmit, reset, setValue } = useForm<SearchProviderRequest>();

  const [showCard, setShowCard] = useState(false);
  const {
    filterPanelRef,
    toggleFilterPanel,
    handleFilterPanelEntering,
    handleFilterPanelEntered,
    handleFilterPanelExited,
  } = useFilterPanelFocus(showCard, setShowCard);

  const [searchCriteria, setSearchCriteria] = useState<SearchProviderRequest>({
    practiceIds: [],
    firstName: "",
    lastName: "",
    email: "",
    hasTemplates : null,
    currentPage: 1,
    itemsPerPage: ITEMS_PER_PAGE,
  });

  const { practiceInfo } = usePracticeSlice();
  const { userInfo } = useAuthSlice();
  const { practiceByUserRoleUserSlice } = useUserReducer();
  const { searchProviderList, usedPracticeProviderIds } = usePracticeProvidersSlice();
  const [createProviderUser, setCreateProviderUser] = useState<{
    message: string;
    show: boolean;
    row: any;
  }>({ message: "", show: false, row: "" });
  const [showOptionPopup, setShowOptionsPopup] = useState<OptionPopupObjectProps>({ message: '', show: false });

  const canCreateProviderUser = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessProviderActions", PROVIDER_LIST_ACTIONS.CREATE_PROVIDER_USER )(state));
  const canViewProvider = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessProviderActions", PROVIDER_LIST_ACTIONS.VIEW )(state));
  const canAccessUsersList = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessApplication", CAN_ACCESS_APPLICATION.USER_MANAGEMENT )(state));
  const canAccessProvidersList = useSelector((state: RootState) => selectIsValueFoundInToken("canManageUsers", USER_ROLES.ROLE_PROVIDER )(state));
  const [fetchPracticesCompleted, setFetchPracticesCompleted] = useState<boolean>(false);
  // CS-1685 - Start
  const canExportProvider = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessExportExcel", EXPORTABLE_ITEMS.PROVIDER )(state));
  const [anchorElMenu, setAnchorElMenu] = useState<null | HTMLElement>(null);

  // --- Design tokens -----------------------
  const listBorderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(15,23,42,0.12)";
  const listPanelBg    = isDark ? "#141720" : "rgba(255,255,255,0.82)";
  const listHoverBg    = isDark ? "rgba(20,184,166,0.09)" : "rgba(20,184,166,0.06)";

  const exportBtnSx = {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color: "#14B8A6",
    borderColor: "rgba(20,184,166,0.55)",
    backgroundColor: theme.palette.mode === "dark" ? "rgba(20,184,166,0.08)" : "rgba(20,184,166,0.04)",
    whiteSpace: "nowrap",
    "& .MuiSvgIcon-root": {
      color: "#14B8A6",
    },
    "&:hover": {
      color: "#fff",
      borderColor: "#14B8A6",
      backgroundColor: "#14B8A6",
      boxShadow: "0 6px 22px rgba(20,184,166,0.28)",
      "& .MuiSvgIcon-root": {
        color: "#fff",
      },
    },
  };

  const listCardSx = {
    borderRadius: "14px",
    border: `1px solid ${listBorderColor}`,
    backgroundColor: listPanelBg,
    backgroundImage: "none",
    backdropFilter: "blur(8px)",
    boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.28)" : "0 12px 28px rgba(15,23,42,0.08)",
  };

  const filterPanelSx = {
    ...listCardSx,
    p: { xs: "14px !important", md: "18px !important" },
    position: "relative",
    zIndex: 20,
    overflow: "visible",
    "& .MuiAutocomplete-root, & .MuiFormControl-root": { width: "100%" },
    "& .MuiTextField-root": { height: "42px", minHeight: "42px !important" },
    "& .MuiOutlinedInput-root, & .MuiInputBase-root, & .MuiAutocomplete-inputRoot, & .MuiAutocomplete-root .MuiOutlinedInput-root": {
      minHeight: "42px !important",
      height: "42px !important",
      borderRadius: "10px !important",
      backgroundColor: `${modalTokens.inputBg} !important`,
      color: modalTokens.textPrimary,
      fontSize: "0.88rem",
      boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
      alignItems: "center",
      boxSizing: "border-box",
    },
    "& .MuiAutocomplete-root .MuiOutlinedInput-root": { padding: "0 38px 0 12px !important" },
    "& .MuiAutocomplete-root .MuiInputBase-input, & .MuiAutocomplete-input, & .MuiOutlinedInput-input": {
      paddingTop: "8px !important",
      paddingBottom: "8px !important",
      height: "24px !important",
      lineHeight: "24px !important",
      boxSizing: "border-box",
    },
    "& .MuiOutlinedInput-input": { paddingLeft: "12px !important", paddingRight: "12px !important" },
    "& .MuiAutocomplete-endAdornment": { top: "50% !important", transform: "translateY(-50%)", right: "8px !important" },
    "& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root fieldset": { borderColor: `${modalTokens.inputBorder} !important` },
    "& .MuiOutlinedInput-root:hover fieldset, & .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: `${modalTokens.accent} !important`, borderWidth: "1px !important" },
    "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: isDark ? "none" : "0 0 0 3px rgba(20,184,166,0.12)" },
    "& .MuiInputBase-input": { color: modalTokens.textPrimary, fontSize: "0.88rem" },
    "& input::selection": { color: modalTokens.textPrimary, backgroundColor: isDark ? "rgba(20,184,166,0.28)" : "rgba(20,184,166,0.2)" },
    "& .MuiOutlinedInput-root:has(input:-webkit-autofill)": { backgroundColor: "#376c9f !important", "& fieldset": { borderColor: "#7fb1df !important" } },
    "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus": { WebkitTextFillColor: "#ffffff", caretColor: "#ffffff", WebkitBoxShadow: "0 0 0 100px #376c9f inset", borderRadius: "10px", transition: "background-color 5000s ease-in-out 0s" },
    "& .MuiInputLabel-root": { color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)", fontSize: "0.88rem" },
    "& .MuiInputLabel-root.Mui-focused": { color: `${modalTokens.accent} !important` },
    "& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)": { transform: "translate(14px, 9px) scale(1)" },
    "& .MuiChip-root": { borderRadius: "999px", backgroundColor: isDark ? "rgba(20,184,166,0.16)" : "rgba(20,184,166,0.1)", color: modalTokens.accent, border: `1px solid ${isDark ? "rgba(20,184,166,0.26)" : "rgba(20,184,166,0.18)"}`, fontWeight: 600 },
    "& .MuiSvgIcon-root": { color: modalTokens.textSecondary },
  };

  const filterFieldLabelSx = {
    fontSize: "0.8rem",
    fontWeight: 600,
    color: modalTokens.textSecondary,
    mb: 0.7,
    height: "16px",
    lineHeight: "16px",
  };

  const primaryActionBtnSx = {
    ...primaryBtnSx,
    color: "#fff",
    "& .MuiTypography-root": { color: "#fff", fontWeight: 700 },
    "& .MuiButton-startIcon": { color: "#fff" },
  };

  const outlineBtnSx = {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color: "#00B8D9",
    borderColor: "rgba(0,184,217,0.55)",
    backgroundColor: isDark ? "rgba(0,184,217,0.08)" : "rgba(0,184,217,0.04)",
    whiteSpace: "nowrap",
    "&:hover": { color: "#fff", borderColor: "#00B8D9", backgroundColor: "#00B8D9", boxShadow: "0 6px 22px rgba(0,184,217,0.28)" },
  };

  const exportMenuItemBtnSx = {
    justifyContent: "flex-start",
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "8px",
    py: 1,
    px: 1.4,
    color: "#2dd4bf",
    borderColor: "rgba(45,212,191,0.35)",
    backgroundColor: isDark ? "rgba(20,184,166,0.13)" : "rgba(20,184,166,0.08)",
    boxShadow: "none",
    "& .MuiButton-startIcon": { mr: 1, "& svg": { fontSize: "18px" } },
    "&:hover": { color: "#fff", borderColor: "#2f9b8f", backgroundColor: "#2f9b8f", boxShadow: "0 10px 24px rgba(20,184,166,0.28)" },
    "&.Mui-disabled": { color: isDark ? "rgba(148,163,184,0.55)" : "rgba(100,116,139,0.7)", borderColor: isDark ? "rgba(148,163,184,0.18)" : "rgba(100,116,139,0.24)", backgroundColor: isDark ? "rgba(148,163,184,0.06)" : "rgba(100,116,139,0.06)" },
  };

  const providerTableSx = {
    "& .MuiTableContainer-root": { borderRadius: "12px", border: "none", backgroundColor: "transparent" },
    "& table": {  borderCollapse: "collapse", borderSpacing: 0 },
    "& .MuiTableCell-root": { borderBottom: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`, py: 1.15, fontSize: "0.84rem" },
    "& .MuiTableHead-root .MuiTableCell-root": { height: "42px"  },
    "& .MuiTableHead-root .MuiTypography-root": { color: "#00B8D9 !important", fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.02em" },
    "& .MuiTableBody-root .MuiTableRow-root": { transition: "background-color 0.18s ease, box-shadow 0.18s ease" },
    "& .MuiTableBody-root .MuiTableRow-root:hover": { backgroundColor: `${listHoverBg} !important` },
    "& .MuiTableBody-root .MuiTableCell-root, & .MuiTableBody-root .MuiTypography-root": { color: modalTokens.textPrimary, fontSize: "0.84rem" },
    "& .MuiTableBody-root .MuiLink-root": { color: "#00B8D9 !important", fontWeight: 600, textDecorationColor: "rgba(0,184,217,0.45)" },
    "& .MuiTableBody-root .MuiLink-root:hover": { color: "#22d3ee !important", textDecorationColor: "#22d3ee" },
    "& .MuiIconButton-root": { color: isDark ? "#94a3b8" : "#475569", borderRadius: "8px", "&:hover": { color: modalTokens.accent, backgroundColor: isDark ? "rgba(20,184,166,0.12)" : "rgba(20,184,166,0.08)" } },
    "& .MuiPaginationItem-root": { borderColor: listBorderColor, color: modalTokens.textSecondary, borderRadius: "8px", "&.Mui-selected": { color: "#fff", backgroundColor: modalTokens.accent, borderColor: modalTokens.accent } },
  };
  // -----------------------------------------------------------------------------

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMenu(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorElMenu(null);
  };

  const handleExport = (exportType: string) => { 
    if (searchCriteria) {
      let dataToSearch = {
        ...searchCriteria,
        currentPage: searchCriteria.currentPage,
        downloadState: true
      }

      ExportToExcel(
        dataToSearch,
        columns,
        dispatch,
        EXPORTABLE_ITEMS.PROVIDER,
      );
    }
    handleMenuClose();
  };
  // CS-1685 - End

  //Mount
  useEffect(() => {
    dispatch(showHideLoadingDialog(true))
  }, []);

  //Unmount
  useEffect(() => () => {
    dispatch(resetSearchedProviderList());
  }, []);

  useEffect(() => {
    //1. When login another user pagination is remain in the history and can redirect that page
    let criteria = getSearchCriteriaFromSessionStorage(CAN_ACCESS_APPLICATION.PRACTICE + "SearchCriteria" + userInfo.userId);
    if ((location.state && location.state.practiceListSearchCriteria) || (location.state && location.state.withSearchHistory && criteria)) {
      if (location.state && location.state.withSearchHistory) {
        //Below navigate line call back to this useEffect - This for refreshing issue when navigate with state.withSearchHistory=true
        navigate("/practice_list", { state: { practiceListSearchCriteria: criteria } });
        return;
      }
      let searchCriteria: SearchProviderRequest = {
        practiceIds: [],
        firstName: "",
        lastName: "",
        email: "",
        hasTemplates : null,
        currentPage: 0,
        itemsPerPage: 0
      };
      if (location.state && location.state.practiceListSearchCriteria) {
        searchCriteria = location.state.practiceListSearchCriteria;
      } else {
        searchCriteria = criteria ? criteria : searchCriteria;
      }
      setSearchCriteria(searchCriteria);
      setValuesToCriteriaFields(searchCriteria);

      searchPracticesList(searchCriteria);
    } else {
      if (userInfo.userRole !== USER_ROLES.ROLE_PRACTICE_USER_PRU) {
        loadInitialPracticeList(1);
      }
    }
  }, [location.state]);

  const setValuesToCriteriaFields = (searchCriteria: SearchProviderRequest) => {
    setValue("practiceIds", searchCriteria.practiceIds);
    setValue("firstName", searchCriteria.firstName);
    setValue("lastName", searchCriteria.lastName);
    setValue("email", searchCriteria.email);
    setValue("hasTemplates", searchCriteria.hasTemplates);
  }

  const loadInitialPracticeList = (pNumber: number = 1) => {
    const data: SearchProviderRequest = {
      practiceIds: [],
      firstName: "",
      lastName: "",
      email: "",
      hasTemplates : null,
      currentPage: pNumber,
      itemsPerPage: ITEMS_PER_PAGE,
    };
    setSearchCriteria(data);
    setValuesToCriteriaFields(data);

    searchPracticesList(data);
  }

  const searchPracticesList = (searchCriteria: SearchProviderRequest) => {
    const normalizedCriteria = providerListSort.normalize(searchCriteria);
    setSearchCriteriaToSessionStorage(CAN_ACCESS_APPLICATION.PROVIDER + "SearchCriteria" + userInfo.userId, normalizedCriteria);
    dispatch(searchProviders(normalizedCriteria));
  }

  const onSubmit = (data: SearchProviderRequest) => {
    const data2: SearchProviderRequest = {
      practiceIds: data.practiceIds,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      hasTemplates: data.hasTemplates,
      currentPage: 1,
      itemsPerPage: ITEMS_PER_PAGE,
      ...providerListSort.pick(searchCriteria),
    }
    setSearchCriteria(data2);

    //Below navigate line call to useEffect[location.state] and search
    navigate("/provider_list", { state: { practiceListSearchCriteria: data2 } });
  }

  const handlePagination = (pageNumber: number) => {
    const data = {
      ...searchCriteria,
      currentPage: pageNumber + 1
    }
    setSearchCriteria(data);

    //Below navigate line call to useEffect[location.state] and search
    navigate("/provider_list", { state: { practiceListSearchCriteria: data } });
  }

  const handleTableSort = (columnId: string, order: "asc" | "desc") => {
    const data = providerListSort.withSort(searchCriteria, columnId, order);
    if (!data) {
      return;
    }
    setSearchCriteria(data);
    navigate("/provider_list", { state: { practiceListSearchCriteria: data } });
  }

  const formattedProviderRows = useMemo(() => {
    if (!searchProviderList?.content) return [];
    
    return searchProviderList.content.map(provider => {
      const isExistingUserProfile = usedPracticeProviderIds?.includes(provider.providerId);
      
      return {
        ...provider,
        providerName: isExistingUserProfile&& canAccessUsersList && canAccessProvidersList ? (
          <Link
            component="button"
            underline="hover"
            variant="body2"
            onClick={() => handleUserListNavigate(provider)}
            color={colors.info[300]}
          >
            {fullName(provider.title, provider.firstName, provider.middleName, provider.lastName)}
          </Link>
        ) : (
          fullName(provider.title, provider.firstName, provider.middleName, provider.lastName)),
        isExistingUserProfile,
        userCreated: isExistingUserProfile
          ? "Yes"
          : provider.approvedStatus !== "APPROVED"
            ? "No (Practice not approved)"
            : "No", 
        templateAvailability: (
          <Chip
            label={provider.hasTemplates ? "Available" : "Not Available"}
            size="small"
            sx={{
              backgroundColor: provider.hasTemplates
                ? "#27ae6022"
                : "#ff4d4d22",
              color: provider.hasTemplates
                ? "#2ecc71"
                : "#ff4d4d",
              fontWeight: "bold",
              px: 1.5,
              borderRadius: "999px",
              fontSize: "0.75rem",
            }}
          />
        )
      };
    });
  }, [searchProviderList, usedPracticeProviderIds]);

  useEffect(() => {
    if (fetchPracticesCompleted && formattedProviderRows) {
      dispatch(showHideLoadingDialog(false))
    }
  }, [formattedProviderRows, fetchPracticesCompleted])

  const handleUserListNavigate = (row: any) => {
    navigate("/user_list", { state: { providerData: { firstName: row.firstName, lastName: row.lastName, entityIds: row.practiceId } } });
  };

  const actionComponent = (row: any): JSX.Element | null => {
    const isDisplay = hideElement(!(canCreateProviderUser && !row.isExistingUserProfile && row.approvedStatus === "APPROVED"));

    // If no buttons should be displayed, return null
    if (isDisplay === 'none' && !row.hasTemplates) {
      return null;
    }

    return (
      <>
        <CustomIconButton
          color="success"
          size="small"
          variant="outlined"
          tooltipMessage="Create User Account"
          tooltipPlacement="top"
          onClick={() => { handleCreateProviderUser(row) }}
          style={{
            display: isDisplay,
          }}
        >
          <PersonAddAltOutlined fontSize="small" />
        </CustomIconButton>

        {row.hasTemplates &&
          <CustomIconButton
            color="info"
            size="small"
            variant="outlined"
            tooltipMessage="Provider Template View"
            tooltipPlacement="top"
            onClick={() => { viewProviderTemplate(row) }}
          >
            <WysiwygOutlined fontSize="small" />
          </CustomIconButton>
        }
      </>
    );
  }

  const handleCreateProviderUser = async (row: any) => {
    setCreateProviderUser({
      message:
        "Do you wish to create a user account for this practice provider? ",
      show: true,
      row: row,
    });
  };

  const viewProviderTemplate = async (row: any) => {
    const response = await dispatch(fetchProviderTemplatesByProvider(row.providerId.toString()));
    if (response.payload.data.length > 0) {
      navigate("/provider_templateDetails", { state: { providerId: row.providerId } });
    } else {
      setShowOptionsPopup({
        show: true,
        variant: "error",
        message: (
          <>
            <strong>No Templates Available:</strong> No templates are configured for this provider.
            Please contact your administrator or ensure the provider setup is complete.
          </>
        ),
        buttons: [
          {
            name: "OK",
            color: "error",
            onClick: () => {
              setShowOptionsPopup({ show: false, message: "" });
            },
          },
        ],
      });
    }
  };

  const confirmCreateProviderUser = () => {
    handleNavigateToCreateUser(createProviderUser.row);
    setCreateProviderUser({ message: "", show: false, row: "" });
  };

  const handleNavigateToCreateUser = (row: any) => {
    dispatch(setSelectedModule("User")); 
    navigate("/user_create", { state: { providerData: { providerId: row.providerId, entityIds: row.practiceId }}});
  };
  const cancelCreateProviderUser = () => {
    setCreateProviderUser({ message: "", show: false, row: "" });
  };

  const practiceOptions = useMemo(() => {
    let options: Option[] = [];
    if (practiceByUserRoleUserSlice && practiceByUserRoleUserSlice.length > 0) {
      const sortedPracticeInfo: PracticeInfo[] = sortNames(practiceByUserRoleUserSlice, 'practiceName');
      sortedPracticeInfo?.map(practice => {
        options.push({ value: practice.practiceId, label: practice.practiceName });
      });
    }
    return options;
  }, [practiceByUserRoleUserSlice]);

  const templateAvailabilityOptions = useMemo(() => {
    let options: { label: string, value: any }[] = [
      { value: true, label: "Available" },
      { value: false, label: "Not Available" },
    ];
    return options;
  }, []);

  useEffect(() => {
    const fetchPractices = async () => {
      const res = await dispatch(getPracticesByUserRoleUserSlice(true));
      if (res) {
        setFetchPracticesCompleted(true);
      }
    };

    fetchPractices();
  }, [dispatch]);

  const legacyItems = [
    { icon: <ModeOutlined color="info" />, text: "Edit" },
    { icon: <VisibilityOutlined color="info" />, text: "Preview" },
    { icon: <Checklist color="success" />, text: "Approve" },
    // { icon: <NoteAddOutlined color="info" />, text: "View Practice Request" }
    { icon: <ArchiveOutlined color="info" />, text: "View Archive Practice" }
  ];

    useEffect(()=>{
      dispatch(fetchUsedPracticeProviderIDs());
    },[])

  const totalRecords = searchProviderList?.totalElements ?? 0;

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          px: { xs: 1, md: 3 },
          py: 2,
        }}
      >
        {/* -- Filter panel -- */}
        <Box sx={{ position: "relative", zIndex: 20, overflow: "visible" }}>
          <Collapse in={showCard} ref={filterPanelRef} onEntering={handleFilterPanelEntering} onEntered={handleFilterPanelEntered} onExited={handleFilterPanelExited}>
            <Card sx={filterPanelSx}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item md={3} xs={12}>
                    <CustomMultipleDropDown
                      control={control}
                      name="practiceIds"
                      label="Practice Name"
                      placeholderText="Select practice"
                      options={practiceOptions}
                      size="small"
                    />
                  </Grid>
                  <Grid item md={3} xs={12}>
                    <CustomTextField
                      control={control}
                      controllerName="firstName"
                      label="First Name"
                      placeholder="Enter first name"
                      size="small"
                    />
                  </Grid>
                  <Grid item md={3} xs={12}>
                    <CustomTextField
                      control={control}
                      controllerName="lastName"
                      label="Last Name"
                      placeholder="Enter last name"
                      size="small"
                    />
                  </Grid>
                  <Grid item md={3} xs={12}>
                    <CustomTextField
                      control={control}
                      controllerName="email"
                      label="Email"
                      placeholder="Enter email"
                      size="small"
                    />
                  </Grid>
                  <Grid item md={3} xs={12}>
                    <CustomDropdownField
                      control={control}
                      name="hasTemplates"
                      label="Template Availability"
                      placeholderText="Select template availability"
                      options={templateAvailabilityOptions}
                    />
                  </Grid>
                  <Grid item md={3} xs={12}>
                    <Button
                      fullWidth
                      size="large"
                      startIcon={<SearchOutlined />}
                      sx={{ ...primaryActionBtnSx, height: "42px", py: 0 }}
                      type="submit"
                      removeUpperCase
                    >
                      Search
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Card>
          </Collapse>
        </Box>

        {/* -- Main list card -- */}
        <Box mt={2} sx={{ position: "relative", zIndex: 1 }}>
          <Card sx={{ ...listCardSx, p: { xs: "14px !important", md: "18px !important" } }}>
            <Grid container justifyContent="space-between" alignItems="center" spacing={1.2}>
              <Grid item xs={12} md>
                <Typography variant="h3" fontWeight={800} color="text.primary">Provider List</Typography>
              </Grid>

              {/* Show / Hide filters */}
              <Grid item xs={12} md="auto">
                <Button
                  variant="outlined"
                  color="info"
                  onClick={toggleFilterPanel}
                  startIcon={<SearchOutlined />}
                  fullWidth
                  sx={outlineBtnSx}
                  removeUpperCase
                >
                  {showCard ? "Hide Filters" : "Show Filters"}
                </Button>
              </Grid>

              {/* CS-1685 - Export */}
              {canExportProvider && (
                <Grid item xs={12} md="auto">
                  {!isMobile && (
                    <Button
                      id="customized-button"
                      aria-controls={Boolean(anchorElMenu) ? "customized-menu" : undefined}
                      aria-haspopup="true"
                      aria-expanded={Boolean(anchorElMenu) ? "true" : undefined}
                      variant="outlined"
                      disableElevation
                      onClick={handleMenuClick}
                      startIcon={<ExitToApp />}
                      sx={exportBtnSx}
                      removeUpperCase
                      endIcon={
                        <Box
                          component="span"
                          sx={{
                            display: "flex",
                            transition: "transform 0.3s ease",
                            transform: anchorElMenu ? "rotate(180deg)" : "rotate(0deg)",
                            padding: "2px",
                          }}
                        >
                          <KeyboardArrowDown />
                        </Box>
                      }
                    >
                      Export
                    </Button>
                  )}

                  {isMobile && (
                    <Button
                      color="success"
                      variant="text"
                      onClick={handleMenuClick}
                      startIcon={<DownloadOutlined fontSize="small" />}
                      sx={exportBtnSx}
                      removeUpperCase
                    >
                      Export
                    </Button>
                  )}

                  <StyledMenu
                    id="customized-menu"
                    MenuListProps={{ "aria-labelledby": "customized-button" }}
                    anchorEl={anchorElMenu}
                    open={Boolean(anchorElMenu)}
                    onClose={handleMenuClose}
                    slotProps={{
                      paper: {
                        sx: {
                          width: "260px",
                          maxWidth: "90vw",
                          mt: 0.75,
                          p: 0.75,
                          borderRadius: "12px",
                          border: `1px solid ${isDark ? "rgba(0,184,217,0.3)" : "rgba(0,184,217,0.22)"}`,
                          backgroundColor: isDark ? "#141720" : "#ffffff",
                          backgroundImage: "none",
                          boxShadow: isDark
                            ? "0 18px 42px rgba(0,0,0,0.45)"
                            : "0 14px 32px rgba(15,23,42,0.14)",
                        },
                      },
                    }}
                  >
                    <Box sx={{ px: 1, mb: 1 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<CloudUpload />}
                        onClick={() => handleExport("searchList")}
                        disabled={totalRecords > 1000}
                        sx={exportMenuItemBtnSx}
                        removeUpperCase
                      >
                        Export Search List
                      </Button>
                    </Box>

                    {totalRecords > 1000 && (
                      <Box
                        sx={{
                          mx: 1,
                          p: 1.5,
                          mt: 1,
                          backgroundColor: "warning.light",
                          borderRadius: 1,
                          color: "warning.contrastText",
                        }}
                      >
                        <Typography variant="body2">
                          Export is available only when the list contains 1,000 records or fewer.
                          Maximum export size: 1000 records.
                        </Typography>
                      </Box>
                    )}
                  </StyledMenu>
                </Grid>
              )}
              {/* CS-1685 - End */}
            </Grid>

            <Table
              columns={columns}
              rows={fetchPracticesCompleted ? formattedProviderRows || [] : []}
              actionComponent={(row: any) => actionComponent(row)}
              emptyText="No Provider(s) Found"
              pageNumber={handlePagination}
              totalPages={fetchPracticesCompleted ? searchProviderList?.totalPages : 0}
              totalElementText={"Records: " + ((fetchPracticesCompleted && searchProviderList?.totalElements) ? searchProviderList?.totalElements : "0")}
              currentPageNumber={searchCriteria.currentPage - 1}
              showMenu={true}
              sortableColumns={providerListSort.sortableColumns}
              serverSideSorting
              onSortChange={handleTableSort}
              controlledSortColumn={providerListSort.controlledColumn(searchCriteria.sortBy)}
              controlledSortOrder={searchCriteria.sortOrder ?? null}
              sx={providerTableSx}
            />
          </Card>
        </Box>
      </Box>

      {showOptionPopup.show &&
        <OptionsPopup
          open={showOptionPopup.show}
          onClose={() => setShowOptionsPopup({ message: "", show: false })}
          variant={showOptionPopup.variant}
          message={<>{showOptionPopup.message}</>}
          buttons={showOptionPopup.buttons}
          title={showOptionPopup.title}
          obj={showOptionPopup.obj}
        />
      }
       <OptionsPopup
        open={createProviderUser && createProviderUser.show}
        onClose={() =>
          setCreateProviderUser({ message: "", show: false, row: "" })
        }
        variant="confirm"
        message={createProviderUser.message}
        buttons={[
          {
            name: "Confirm",
            color: "primary",
            onClick: confirmCreateProviderUser,
          },
          {
            name: "Cancel",
            color: "secondary",
            onClick: cancelCreateProviderUser,
          },
        ]}
      />
    </>
  );

}

export default ProviderList;